import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, StyledBody } from 'baseui/card';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { HeadingLevel, Heading } from 'baseui/heading';
import { PieChart, Pie, Cell, Legend, Tooltip as RechartsTooltip, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { AppContext, UserContext } from 'commons-ui';
import { Checkbox } from 'baseui/checkbox';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, SIZE } from 'baseui/modal';
import { Notification, KIND } from 'baseui/notification';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { URL, USER_SEGMENT_RESOURCES } from '../../../helper';
import useService from '../../../useService';
import { ESegmentEvaluationStatus } from '../../../../../../constant';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface SegmentData {
	name?: string;
	_id?: string;
	queryText?: string;
	updatedAt?: number;
	evaluationResults?: {
		affectedUsersCount: number;
		totalUsersEvaluated: number;
		featureSegmentEvaluationResults?: Record<string, number>;
		jobEvalStatus?: ESegmentEvaluationStatus;
	};
	[key: string]: any;
}

const COLORS: string[] = [
	'#0088FE', // Blue
	'#FFBB28', // Orange
	'#00C49F', // Teal
	'#FF8042', // Coral
	'#8884d8', // Purple
	'#82ca9d', // Green
	'#FF6347', // Tomato
	'#4682B4', // SteelBlue
	'#32CD32', // LimeGreen
	'#DA70D6', // Orchid
	'#FFD700' // Gold
];

// Custom Tooltip for RadialBarChart
const CustomRadialTooltip = ({ active, payload }: any) => {
	if (active && payload && payload.length) {
		const data: any = payload[0].payload; // Access the original data object
		return (
			<div style={{ backgroundColor: '#666', padding: '5px 10px', borderRadius: '3px', color: 'white' }}>
				<p>{`${data.name}: ${new Intl.NumberFormat().format(data.value)}`}</p>
			</div>
		);
	}
	return null;
};

const Evaluate: React.FC = () => {
	const location = useLocation<{ rowData: SegmentData }>();
	const history = useHistory();
	const { showAlert } = useContext(AppContext);
	const { user } = useContext(UserContext);
	const { requestActivateSegment } = useService();
	const [isActivating, setIsActivating] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

	// Function to format timestamp to IST (moved inside component)
	const formatTimestampIST = (timestamp: number | undefined): string => {
		if (!timestamp) {
			return 'N/A';
		}
		try {
			return dayjs(timestamp).tz('Asia/Kolkata').format('MMM D, YYYY h:mm A');
		} catch (error) {
			showAlert('Error formatting timestamp:', { type: 'error' });
			return 'Invalid Date';
		}
	};

	// Get the segment data from location state
	const rowData: SegmentData = location.state?.rowData || {};

	// Extract evaluation data and status
	const { evaluationResults } = rowData;
	const featureSegmentEvaluationResults: Record<string, number> = evaluationResults?.featureSegmentEvaluationResults || {};
	const jobStatus: ESegmentEvaluationStatus | undefined = evaluationResults?.jobEvalStatus;

	// Determine content based on jobStatus
	const showNoDataMessage: boolean = !jobStatus || !evaluationResults;
	const showInProgressMessage: boolean = jobStatus === ESegmentEvaluationStatus.STARTED;
	const showFailedMessage: boolean = jobStatus === ESegmentEvaluationStatus.FAILED;
	const showCompletedData: boolean = jobStatus === ESegmentEvaluationStatus.COMPLETED;

	// Check if data counts are valid (only relevant if COMPLETED)
	const isUserDataValid: boolean = (showCompletedData && evaluationResults && evaluationResults.totalUsersEvaluated > 0) || false;
	const isFeatureDataValid: boolean =
		(showCompletedData && evaluationResults && Object.keys(evaluationResults.featureSegmentEvaluationResults || {}).length > 0) ||
		false;

	// Total count for feature percentage calculation
	const totalAffectedUsers: number = showCompletedData ? evaluationResults?.affectedUsersCount || 0 : 0;

	// Prepare data for charts (only if COMPLETED and data is valid)
	const pieData: { name: string; value: number }[] = isUserDataValid
		? [
				{ name: 'Affected Users', value: evaluationResults?.affectedUsersCount || 0 },
				{
					name: 'Unaffected Users',
					value: (evaluationResults?.totalUsersEvaluated || 0) - (evaluationResults?.affectedUsersCount || 0)
				}
		  ]
		: [];

	const radialBarData: { name: string; value: number }[] = isFeatureDataValid
		? Object.entries(featureSegmentEvaluationResults).map(([name, value]) => ({
				name,
				value: Number(value),
				fill: COLORS[Object.keys(featureSegmentEvaluationResults).indexOf(name) % COLORS.length]
		  }))
		: [];

	// Prepare data for feature table (only if COMPLETED and data is valid)
	const featureTableData: { name: string; count: number; percentage: string | number }[] = isFeatureDataValid
		? Object.entries(featureSegmentEvaluationResults).map(([key, value]) => ({
				name: key,
				count: Number(value),
				percentage: totalAffectedUsers > 0 ? ((Number(value) / totalAffectedUsers) * 100).toFixed(2) : 0
		  }))
		: [];

	// Handle activation confirmation
	const handleActivate: () => void = async () => {
		// Activation only allowed if status is COMPLETED
		if (jobStatus !== ESegmentEvaluationStatus.COMPLETED) {
			showAlert('Cannot activate segment until evaluation is successfully completed.', { type: 'error' });
			return;
		}
		if (!isVerified) {
			showAlert('Please verify that you have reviewed the data before proceeding', { type: 'warning' });
			return;
		}
		setIsConfirmationOpen(true);
	};

	// Handle actual activation after confirmation
	const handleConfirmedActivation: () => void = async () => {
		setIsConfirmationOpen(false);
		setIsActivating(true);
		try {
			await requestActivateSegment(rowData, showAlert, user);
			history.push(`${URL[USER_SEGMENT_RESOURCES.listSegment]}/list`);
		} catch (error) {
			showAlert('Error activating segment:', { type: 'error' });
		} finally {
			setIsActivating(false);
		}
	};

	// Handle cancel
	const handleCancel: () => void = () => {
		history.push(`${URL[USER_SEGMENT_RESOURCES.listSegment]}/list`);
	};

	// Disable button if not completed, or not verified
	const activateButtonDisabled: boolean = jobStatus !== ESegmentEvaluationStatus.COMPLETED || !isVerified;

	const renderEvaluationContent: () => React.ReactNode = () => {
		if (showNoDataMessage) {
			return (
				<Notification kind={KIND.warning} overrides={{ Body: { style: { width: 'auto' } } }}>
					No draft evaluation result found, please retrigger the evaluation or edit your draft segment. Please connect with
					platform team for more info.
				</Notification>
			);
		}

		if (showInProgressMessage) {
			return (
				<Notification kind={KIND.warning} overrides={{ Body: { style: { width: 'auto' } } }}>
					Segment evaluation is in progress. Please wait 5-10 minutes and refresh.
				</Notification>
			);
		}

		if (showFailedMessage) {
			return (
				<Notification kind={KIND.negative} overrides={{ Body: { style: { width: 'auto' } } }}>
					Segment evaluation failed. Please checkout slack channel #user-segmentation-prod-notification and contact the
					platform team
				</Notification>
			);
		}

		if (showCompletedData) {
			return (
				<>
					{/* User Evaluation Results */}
					<Block marginBottom='20px'>
						<Card>
							<StyledBody>
								<HeadingLevel>
									<Heading styleLevel={3}>User Evaluation Results</Heading>
									{!isUserDataValid ? (
										<Notification kind={KIND.warning} overrides={{ Body: { style: { width: 'auto' } } }}>
											User evaluation completed but data is zero or unavailable.
										</Notification>
									) : (
										<>
											<Block height='300px'>
												<ResponsiveContainer width='100%' height='100%'>
													<PieChart>
														<Pie
															data={pieData}
															cx='50%'
															cy='50%'
															labelLine
															label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
															outerRadius={100}
															fill='#8884d8'
															dataKey='value'>
															{pieData.map((entry, index) => (
																<Cell
																	key={`cell-${entry.name}`}
																	fill={COLORS[index % COLORS.length]}
																/>
															))}
														</Pie>
														<RechartsTooltip
															formatter={(value) => new Intl.NumberFormat().format(Number(value))}
														/>
														<Legend />
													</PieChart>
												</ResponsiveContainer>
											</Block>
											<Block marginTop='10px'>
												<p>
													<strong>Total Users Evaluated:</strong>{' '}
													{evaluationResults?.totalUsersEvaluated || 0}
												</p>
												<p>
													<strong>Affected Users:</strong> {evaluationResults?.affectedUsersCount || 0}
												</p>
												<p>
													<strong>Affected User Percentage:</strong>{' '}
													{(
														((evaluationResults?.affectedUsersCount ?? 0) /
															(evaluationResults?.totalUsersEvaluated ?? 1)) *
															100 || 0
													).toFixed(2)}
													%
												</p>
											</Block>
										</>
									)}
								</HeadingLevel>
							</StyledBody>
						</Card>
					</Block>

					{/* Feature Segment Evaluation Results */}
					<Block marginBottom='20px'>
						<Card>
							<StyledBody>
								<HeadingLevel>
									<Heading styleLevel={3}>Feature Segment Evaluation Results</Heading>
									{!isFeatureDataValid ? (
										<Notification kind={KIND.warning} overrides={{ Body: { style: { width: 'auto' } } }}>
											Feature segment evaluation completed but data is zero or unavailable.
										</Notification>
									) : (
										<>
											{/* Feature Table */}
											<Block marginBottom='scale600'>
												<TableBuilder data={featureTableData}>
													<TableBuilderColumn
														header='Feature Segment Name'
														overrides={{
															TableHeadCell: { style: { textAlign: 'center' } },
															TableBodyCell: { style: { textAlign: 'center' } }
														}}>
														{(row: any) => row.name}
													</TableBuilderColumn>
													<TableBuilderColumn
														header='Affected Users'
														numeric
														overrides={{
															TableHeadCell: { style: { textAlign: 'center' } },
															TableBodyCell: { style: { textAlign: 'center' } }
														}}>
														{(row: any) => row.count}
													</TableBuilderColumn>
													<TableBuilderColumn
														header='Impact %'
														numeric
														overrides={{
															TableHeadCell: { style: { textAlign: 'center' } },
															TableBodyCell: { style: { textAlign: 'center' } }
														}}>
														{(row: any) => `${row.percentage}%`}
													</TableBuilderColumn>
												</TableBuilder>
											</Block>

											{/* Radial Bar Chart */}
											<Block height='300px'>
												<ResponsiveContainer width='100%' height='100%'>
													<RadialBarChart
														cx='50%'
														cy='50%'
														innerRadius='20%'
														outerRadius='80%'
														barSize={10}
														data={radialBarData}>
														<RadialBar background dataKey='value' />
														<Legend
															iconSize={10}
															layout='vertical'
															verticalAlign='middle'
															align='right'
														/>
														{/* Use custom tooltip content */}
														<RechartsTooltip content={<CustomRadialTooltip />} />
													</RadialBarChart>
												</ResponsiveContainer>
											</Block>
										</>
									)}
								</HeadingLevel>
							</StyledBody>
						</Card>
					</Block>
				</>
			);
		}

		return null; // Should not happen if status logic is correct
	};

	return (
		<Block
			padding='20px'
			height='auto'
			minHeight='100%'
			overflow='auto'
			data-page='evaluate'
			overrides={{
				Block: {
					style: {
						height: 'auto',
						minHeight: '100%',
						overflow: 'auto',
						position: 'relative',
						maxHeight: 'none'
					}
				}
			}}>
			<HeadingLevel>
				<Block marginBottom='16px'>
					<Heading
						styleLevel={3}
						overrides={{
							Block: {
								style: {
									marginTop: '0',
									marginBottom: '0',
									fontSize: '20px',
									fontWeight: '500',
									fontStyle: 'bold'
								}
							}
						}}>
						Segment Evaluation Results: {rowData.name}
					</Heading>
				</Block>

				{/* Segment Details Section */}
				<Block marginBottom='20px'>
					<Card>
						<StyledBody>
							<HeadingLevel>
								<Heading styleLevel={6}>Segment Details</Heading>
								<Block>
									<p>
										<strong>Query Text:</strong> {rowData.queryText || 'N/A'}
									</p>
									<p>
										<strong>Last Updated:</strong> {formatTimestampIST(rowData.updatedAt)}
									</p>
								</Block>
							</HeadingLevel>
						</StyledBody>
					</Card>
				</Block>

				{/* Render content based on job status */}
				{renderEvaluationContent()}

				{/* Verification and Buttons - Only visible if status is COMPLETED */}
				{showCompletedData && (
					<Block display='flex' flexDirection='column' alignItems='flex-end' marginTop='20px'>
						<Block marginBottom='10px' width='100%'>
							<Checkbox checked={isVerified} onChange={() => setIsVerified(!isVerified)} labelPlacement='right'>
								I have verified the above data and want to proceed with activation
							</Checkbox>
						</Block>
						<Block display='flex'>
							<Block marginRight='10px'>
								<Button kind='secondary' onClick={handleCancel}>
									Cancel
								</Button>
							</Block>
							<Button onClick={handleActivate} isLoading={isActivating} disabled={activateButtonDisabled}>
								Activate Segment
							</Button>
						</Block>
					</Block>
				)}
			</HeadingLevel>

			{/* Confirmation Modal */}
			<Modal isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} size={SIZE.default} role='dialog'>
				<ModalHeader>Confirmation</ModalHeader>
				<ModalBody>
					Are you sure you want to activate this segment? Please proceed carefully.
					<br />
					<br />
					Segment Name: {rowData.name}
					<br />
					Segment Type: {rowData.SegmentType}
				</ModalBody>
				<ModalFooter>
					<ModalButton kind='tertiary' onClick={() => setIsConfirmationOpen(false)}>
						Cancel
					</ModalButton>
					<ModalButton onClick={handleConfirmedActivation}>Activate</ModalButton>
				</ModalFooter>
			</Modal>
		</Block>
	);
};

export default Evaluate;
